/**
 * SuperAdmin Pool Queue
 * Lists pool healer accounts pending SuperAdmin approval.
 * Approve/reject always go through ConfirmModal — never auto-fire.
 * Data: GET /api/superadmin/healer-approvals?status=<status> (User docs)
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  getPoolHealerApprovals,
  approvePoolHealer,
  rejectPoolHealer,
} from '../../utils/api';
import {
  FiX, FiCheck, FiXCircle, FiChevronRight,
  FiMail, FiCalendar, FiAward,
  FiLoader, FiRefreshCw,
} from 'react-icons/fi';
import { poolRejectMessage } from '../../utils/poolRejectMessage';

const FILTERS = ['All', 'Pending', 'Approved', 'Rejected'];

const FILTER_STATUS = {
  All:      null,
  Pending:  'pending_superadmin',
  Approved: 'approved',
  Rejected: 'rejected',
};

const STATUS_STYLES = {
  pending_superadmin: { bg: 'rgba(230,126,34,0.1)', color: '#e67e22', label: 'Pending SA' },
  approved:           { bg: 'rgba(39,174,96,0.1)',  color: '#27ae60', label: 'Approved'   },
  rejected:           { bg: 'rgba(231,76,60,0.1)',  color: '#e74c3c', label: 'Rejected'   },
};

const CERT_LEVEL_LABELS = {
  basic:                     'Basic Pranic Healer',
  advanced:                  'Advanced Pranic Healer',
  psychotherapy:             'Pranic Psychotherapist',
  arhatic:                   'Arhatic Yoga Practitioner',
  certified_healer:          'Certified Pranic Healer',
  advanced_certified:        'Associate Certified Pranic Healer',
  certified_psychotherapist: 'Certified Pranic Psychotherapist',
};

const STYLE_ID = 'sa-pool-queue';
if (!document.getElementById(STYLE_ID)) {
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    @keyframes pq-spin   { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes pq-fadein { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
  `;
  document.head.appendChild(el);
}

function relativeTime(iso) {
  if (!iso) return '—';
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60)    return 'Just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function normaliseHealer(raw) {
  return {
    id:           String(raw._id),
    name:         [raw.firstName, raw.lastName].filter(Boolean).join(' '),
    email:        raw.email        || '',
    city:         raw.city         || '',
    certLevel:    raw.certLevel    || '',
    certPhotoUrl: raw.certPhotoUrl || null,
    registeredAt: raw.createdAt,
    healerType:   raw.healerType   || 'pool',
    approvalStatus: raw.approvalStatus,
    isExistingHealer: !!raw.isExistingHealer,   // Item 29 — existing healer applying from the app
  };
}

// ─── Toast ───────────────────────────────────────────────────────────────────

const TOAST_COLORS = {
  success: { bg: '#27ae60', Icon: FiCheck   },
  error:   { bg: '#e74c3c', Icon: FiXCircle },
};

const Toast = ({ type, message }) => {
  const { bg, Icon } = TOAST_COLORS[type] || TOAST_COLORS.success;
  return (
    <div style={{
      position: 'fixed', top: '20px', right: '20px', zIndex: 600,
      background: bg, color: '#fff', borderRadius: '8px',
      padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '9px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.18)', fontSize: '13px', fontWeight: '600',
      animation: 'pq-fadein 0.2s ease', maxWidth: '320px',
    }}>
      <Icon style={{ fontSize: '15px', flexShrink: 0 }} />
      {message}
    </div>
  );
};

// ─── List loading / error ─────────────────────────────────────────────────────

const ListLoadingView = ({ slow }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '200px', gap: '10px', color: '#888' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <FiLoader style={{ fontSize: '18px', animation: 'pq-spin 1s linear infinite' }} />
      <span style={{ fontSize: '13px' }}>Loading pool healers…</span>
    </div>
    {slow && (
      <div style={{ fontSize: '12px', color: '#e67e22', textAlign: 'center', maxWidth: '280px', lineHeight: '1.6' }}>
        Backend is waking up…<br />This takes ~30 seconds on first load. Please wait.
      </div>
    )}
  </div>
);

const ListErrorView = ({ message, onRetry }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '200px', gap: '12px', padding: '24px', textAlign: 'center' }}>
    <div style={{ fontSize: '13px', color: '#e74c3c', maxWidth: '300px', lineHeight: '1.5' }}>{message}</div>
    <button
      type="button"
      onClick={onRetry}
      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '7px', border: 'none', background: '#1a6b5a', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' }}
    >
      <FiRefreshCw style={{ fontSize: '12px' }} /> Retry
    </button>
  </div>
);

// ─── Confirm Modal ────────────────────────────────────────────────────────────

const ConfirmModal = ({ title, body, confirmLabel, confirmColor, onConfirm, onCancel }) => (
  <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
    <div style={{ background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '420px', padding: '28px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
      <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px' }}>{title}</div>
      <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', marginBottom: '24px' }}>{body}</div>
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          style={{ padding: '10px 20px', borderRadius: '7px', border: '1px solid #ddd', background: '#fff', color: '#555', fontSize: '13px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          style={{ padding: '10px 20px', borderRadius: '7px', border: 'none', background: confirmColor, color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', minHeight: '44px' }}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

// ─── Detail Panel ─────────────────────────────────────────────────────────────

const DetailPanel = ({ healer, onClose, onApprove, onReject, actionLoading }) => {
  const [rejectReason,     setRejectReason]     = useState('');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal,  setShowRejectModal]  = useState(false);
  const busy = actionLoading === healer.id;

  return (
    <>
      {showApproveModal && (
        <ConfirmModal
          title="Approve pool healer"
          body={healer.isExistingHealer
            ? `Add ${healer.name} to the LifeChiAI pool? Their own practice is unchanged and no message is sent.`
            : `Activate ${healer.name} as a LifeChiAI pool healer? They will receive a WhatsApp notification.`}
          confirmLabel="Approve"
          confirmColor="#27ae60"
          onConfirm={() => { setShowApproveModal(false); onApprove(healer.id); }}
          onCancel={() => setShowApproveModal(false)}
        />
      )}
      {showRejectModal && (
        <ConfirmModal
          title="Reject pool healer"
          body={healer.isExistingHealer
            ? `This will reject ${healer.name}'s pool application. No message is sent; the reason is kept internally.`
            : `This will reject ${healer.name} and notify ${healer.email} with your reason.`}
          confirmLabel="Reject"
          confirmColor="#e74c3c"
          onConfirm={() => { setShowRejectModal(false); onReject(healer.id, rejectReason); }}
          onCancel={() => setShowRejectModal(false)}
        />
      )}

      <div style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 2px 12px rgba(0,0,0,0.10)', overflow: 'hidden' }}>

        {/* Panel header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a2e' }}>{healer.name}</div>
            <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>{healer.city || '—'}</div>
          </div>
          <span style={{
            fontSize: '11px', fontWeight: '700', borderRadius: '5px', padding: '4px 10px',
            background: STATUS_STYLES[healer.approvalStatus]?.bg    || '#f0f0f0',
            color:      STATUS_STYLES[healer.approvalStatus]?.color || '#888',
          }}>
            {STATUS_STYLES[healer.approvalStatus]?.label}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close detail"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '44px', minWidth: '44px' }}
          >
            <FiX />
          </button>
        </div>

        {/* Quick-ref strip */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', padding: '12px 20px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
          {[
            { icon: FiMail,     val: healer.email                                              },
            { icon: FiAward,    val: CERT_LEVEL_LABELS[healer.certLevel] || healer.certLevel   },
            { icon: FiCalendar, val: relativeTime(healer.registeredAt)                         },
          ].filter(({ val }) => val).map(({ icon: Icon, val }) => (
            <div key={val} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Icon style={{ color: '#bbb', fontSize: '12px', flexShrink: 0 }} />
              <span style={{ fontSize: '12px', color: '#555' }}>{val}</span>
            </div>
          ))}
        </div>

        {/* Detail sections */}
        <div style={{ padding: '20px 20px 10px' }}>
          {[
            {
              title: 'Personal details',
              fields: {
                'Full name': healer.name  || '—',
                'Email':     healer.email || '—',
                'City':      healer.city  || '—',
              },
            },
            {
              title: 'Certification',
              fields: {
                'Level': CERT_LEVEL_LABELS[healer.certLevel] || healer.certLevel || '—',
              },
            },
          ].map(({ title, fields }) => (
            <div key={title} style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a2e', marginBottom: '8px' }}>{title}</div>
              <div style={{ background: '#f8f9f8', borderRadius: '7px', padding: '10px 12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                {Object.entries(fields).map(([label, value]) => (
                  <div key={label}>
                    <div style={{ fontSize: '10px', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
                    <div style={{ fontSize: '12px', color: '#333', fontWeight: '500', marginTop: '2px', wordBreak: 'break-all' }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Certificate photo */}
        {healer.certPhotoUrl && (
          <div style={{ padding: '0 20px 16px' }}>
            <div style={{ fontSize: '10px', color: '#aaa', fontWeight: '600',
              textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>
              Certificate Photo
            </div>
            <a href={healer.certPhotoUrl} target="_blank" rel="noopener noreferrer">
              <img src={healer.certPhotoUrl} alt="Certificate"
                style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px',
                  border: '1px solid #e0e0e0', cursor: 'pointer', display: 'block' }} />
            </a>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
              Click to open full size
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ padding: '16px 20px 20px', borderTop: '1px solid #f0f0f0' }}>
          {healer.approvalStatus === 'pending_superadmin' && (
            <>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '6px' }}>
                  Rejection reason <span style={{ color: '#aaa', fontWeight: '400' }}>(required to reject)</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Explain why this application is being rejected…"
                  rows={3}
                  maxLength={500}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '7px', border: '1px solid #ddd', fontSize: '13px', color: '#333', resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.5', outline: 'none' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => !busy && setShowApproveModal(true)}
                  disabled={busy}
                  style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', background: busy ? '#95d4b3' : '#27ae60', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: busy ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', minHeight: '44px' }}
                >
                  {busy ? <FiLoader style={{ animation: 'pq-spin 1s linear infinite' }} /> : <FiCheck />}
                  {busy ? 'Processing…' : 'Approve'}
                </button>
                <button
                  type="button"
                  onClick={() => !busy && rejectReason.trim() && setShowRejectModal(true)}
                  disabled={busy || !rejectReason.trim()}
                  style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', background: (!busy && rejectReason.trim()) ? '#e74c3c' : '#f0f0f0', color: (!busy && rejectReason.trim()) ? '#fff' : '#bbb', fontSize: '13px', fontWeight: '700', cursor: (!busy && rejectReason.trim()) ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', minHeight: '44px', transition: 'background 0.15s, color 0.15s' }}
                >
                  <FiXCircle /> Reject
                </button>
              </div>
            </>
          )}

          {healer.approvalStatus === 'approved' && (
            <div style={{ background: 'rgba(39,174,96,0.08)', borderRadius: '8px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FiCheck style={{ color: '#27ae60', fontSize: '16px', flexShrink: 0 }} />
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#27ae60' }}>Approved and active</div>
            </div>
          )}

          {healer.approvalStatus === 'rejected' && (
            <div style={{ background: 'rgba(231,76,60,0.07)', borderRadius: '8px', padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiXCircle style={{ color: '#e74c3c', fontSize: '15px', flexShrink: 0 }} />
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#e74c3c' }}>Rejected</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
  filterRow: { display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' },

  chip: (active) => ({
    padding: '7px 16px', borderRadius: '20px', border: 'none',
    background: active ? '#1a6b5a' : '#fff',
    color:      active ? '#fff'    : '#555',
    fontSize: '12px', fontWeight: active ? '700' : '500',
    cursor: 'pointer', minHeight: '36px',
    boxShadow: active ? 'none' : '0 1px 3px rgba(0,0,0,0.08)',
    transition: 'background 0.15s, color 0.15s',
  }),

  tableWrap: {
    background: '#fff', borderRadius: '10px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    overflow: 'hidden', marginBottom: '20px',
  },

  tableHead: {
    display: 'grid',
    gridTemplateColumns: '2fr 2fr 1fr 140px 80px 72px 36px',
    padding: '10px 16px',
    background: '#f8f9f8', borderBottom: '1px solid #eee',
    fontSize: '11px', fontWeight: '700', color: '#aaa',
    textTransform: 'uppercase', letterSpacing: '0.5px', gap: '8px',
  },

  tableRow: (selected) => ({
    display: 'grid',
    gridTemplateColumns: '2fr 2fr 1fr 140px 80px 72px 36px',
    padding: '13px 16px',
    borderBottom: '1px solid #f6f6f6',
    cursor: 'pointer', gap: '8px', alignItems: 'center',
    background: selected ? 'rgba(26,107,90,0.05)' : '#fff',
    transition: 'background 0.12s',
    minHeight: '52px',
  }),

  cellPrimary:   { fontSize: '13px', fontWeight: '600', color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cellSecondary: { fontSize: '12px', color: '#888',    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cellMeta:      { fontSize: '11px', color: '#bbb' },

  certBadge: {
    fontSize: '10px', fontWeight: '600', color: '#2980b9',
    background: 'rgba(41,128,185,0.1)', borderRadius: '4px',
    padding: '2px 6px', display: 'inline-block',
  },

  poolBadge: {
    fontSize: '10px', fontWeight: '700', color: '#1a6b5a',
    background: 'rgba(26,107,90,0.1)', borderRadius: '4px',
    padding: '2px 6px', display: 'inline-block', marginTop: '2px',
  },

  statusBadge: (status) => ({
    fontSize: '10px', fontWeight: '700', borderRadius: '5px', padding: '2px 7px',
    background: STATUS_STYLES[status]?.bg    || '#f0f0f0',
    color:      STATUS_STYLES[status]?.color || '#888',
    display: 'inline-block', whiteSpace: 'nowrap',
  }),

  chevron: { color: '#ccc', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' },
  empty:   { padding: '32px', textAlign: 'center', color: '#bbb', fontSize: '13px' },
};

// ─── Component ────────────────────────────────────────────────────────────────

const PoolQueue = () => {
  const [activeFilter,  setActiveFilter]  = useState('Pending');
  const [selectedId,    setSelectedId]    = useState(null);
  const [healers,       setHealers]       = useState([]);
  const [listLoading,   setListLoading]   = useState(true);
  const [listError,     setListError]     = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast,         setToast]         = useState(null);
  const [slowLoad,      setSlowLoad]      = useState(false);
  const slowTimerRef  = useRef(null);
  const retryTimerRef = useRef(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchHealers = async () => {
    setListLoading(true);
    setListError(null);
    setSlowLoad(false);
    slowTimerRef.current = setTimeout(() => {
      setSlowLoad(true);
      retryTimerRef.current = setTimeout(() => fetchHealers(), 30000);
    }, 10000);
    try {
      const [pendingRes, approvedRes, rejectedRes] = await Promise.all([
        getPoolHealerApprovals('pending_superadmin'),
        getPoolHealerApprovals('approved'),
        getPoolHealerApprovals('rejected'),
      ]);
      const all = [
        ...(pendingRes.data.data  || []),
        ...(approvedRes.data.data || []),
        ...(rejectedRes.data.data || []),
      ].filter(h => h.healerType === 'pool');
      setHealers(all.map(normaliseHealer));
    } catch (err) {
      setListError(err.response?.data?.message || 'Failed to load pool healers. Check your connection.');
    } finally {
      clearTimeout(slowTimerRef.current);
      clearTimeout(retryTimerRef.current);
      setListLoading(false);
    }
  };

  useEffect(() => { fetchHealers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = FILTER_STATUS[activeFilter] === null
    ? healers
    : healers.filter(h => h.approvalStatus === FILTER_STATUS[activeFilter]);

  const selectedHealer = healers.find(h => h.id === selectedId) || null;

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await approvePoolHealer(id);
      const name = healers.find(h => h.id === id)?.name || '';
      setHealers(prev => prev.map(h =>
        h.id === id ? { ...h, approvalStatus: 'approved' } : h
      ));
      setSelectedId(null);
      showToast('success', `Approved — ${name} is now active`);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Approval failed. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id, reason) => {
    setActionLoading(id);
    try {
      await rejectPoolHealer(id, reason);
      const rejected = healers.find(h => h.id === id);
      const name = rejected?.name || '';
      setHealers(prev => prev.map(h =>
        h.id === id ? { ...h, approvalStatus: 'rejected' } : h
      ));
      setSelectedId(null);
      showToast('success', poolRejectMessage(name, !!rejected?.isExistingHealer));
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Rejection failed. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div>
      {toast && <Toast type={toast.type} message={toast.message} />}

      {/* Filter chips */}
      <div style={S.filterRow}>
        {FILTERS.map(f => {
          const count = FILTER_STATUS[f] === null
            ? healers.length
            : healers.filter(h => h.approvalStatus === FILTER_STATUS[f]).length;
          return (
            <button
              key={f}
              type="button"
              style={S.chip(activeFilter === f)}
              onClick={() => { setActiveFilter(f); setSelectedId(null); }}
            >
              {f}&nbsp;<span style={{ opacity: 0.7 }}>({count})</span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div style={S.tableWrap}>
        <div style={S.tableHead}>
          <span>Name</span>
          <span>Email</span>
          <span>City</span>
          <span>Cert Level</span>
          <span>Registered</span>
          <span>Status</span>
          <span></span>
        </div>

        {listLoading && <ListLoadingView slow={slowLoad} />}
        {listError   && <ListErrorView message={listError} onRetry={fetchHealers} />}

        {!listLoading && !listError && filtered.length === 0 && (
          <div style={S.empty}>No pool healers pending super admin approval.</div>
        )}

        {!listLoading && !listError && filtered.map(healer => (
          <div
            key={healer.id}
            style={S.tableRow(selectedId === healer.id)}
            onClick={() => setSelectedId(prev => prev === healer.id ? null : healer.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setSelectedId(prev => prev === healer.id ? null : healer.id)}
          >
            <div style={{ minWidth: 0 }}>
              <div style={S.cellPrimary} title={healer.name}>{healer.name}</div>
              <span style={S.poolBadge}>Pool Healer</span>
            </div>
            <div style={S.cellSecondary}>{healer.email}</div>
            <div style={S.cellSecondary}>{healer.city || '—'}</div>
            <div><span style={S.certBadge}>{CERT_LEVEL_LABELS[healer.certLevel] || healer.certLevel || '—'}</span></div>
            <div style={S.cellMeta}>{relativeTime(healer.registeredAt)}</div>
            <div><span style={S.statusBadge(healer.approvalStatus)}>{STATUS_STYLES[healer.approvalStatus]?.label}</span></div>
            <div style={S.chevron}><FiChevronRight /></div>
          </div>
        ))}
      </div>

      {/* Detail panel */}
      {selectedHealer && (
        <DetailPanel
          healer={selectedHealer}
          onClose={() => setSelectedId(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          actionLoading={actionLoading}
        />
      )}
    </div>
  );
};

export default PoolQueue;
